import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { 
  getAuthToken, 
  setAuthToken, 
  setUserData, 
  clearAuthData 
} from '../utils/authStorage';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setUserData(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, tenant_id, is_super_admin } = response.data;
    // Merge super admin + tenant flags onto user object so UI can route accordingly
    const enriched = { ...userData, tenant_id, is_super_admin: !!is_super_admin };
    setAuthToken(newToken);
    setToken(newToken);
    setUser(enriched);
    setUserData(enriched);
    return enriched;
  };

  const register = async (email, password, full_name, role = 'accountant') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name,
      role
    });
    const { token: newToken, user: userData } = response.data;
    setAuthToken(newToken);
    setToken(newToken);
    setUser(userData);
    setUserData(userData);
    return userData;
  };

  const signup = async (payload) => {
    const response = await axios.post(`${API}/signup`, payload);
    const { token: newToken, user: userData, tenant } = response.data;
    const enriched = { ...userData, tenant_id: tenant.id, is_super_admin: false };
    setAuthToken(newToken);
    setToken(newToken);
    setUser(enriched);
    setUserData(enriched);
    return { user: enriched, tenant, trial_ends_at: response.data.trial_ends_at };
  };

  const logout = () => {
    clearAuthData();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const getAuthHeader = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};